import { faSpinner, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { faSearch, faQuestion } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useRef, useState } from 'react'
import { GeocodeAddress, MapboxPlaceType } from '@citruscamps/citrus-client'
import { GeocoderResponse, useGeocoderSearch } from '../../hooks/useGeocoderSearch'

interface MapboxGeocoderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  onSelect?: (result?: GeocodeAddress) => void | Promise<void>
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onAddAddress?: (value?: string) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  result?: GeocodeAddress
  inputId?: string
  inputName?: string
  invalid?: boolean
  proximity?: number[]
  bbox?: string
  types?: MapboxPlaceType[]
  readOnly?: boolean
}

interface GeocoderState {
  results: GeocodeAddress[]
  selected?: GeocodeAddress | null
  focus: number | null
  loading: boolean
  searchTime: Date
  query: string
}

export const MapboxGeocoder = (props: MapboxGeocoderProps): React.ReactElement => {
  const access_token: string = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || ''
  const {
    onSelect: handleSelect,
    onBlur: handleBlur,
    onAddAddress: handleAddAddress,
    onChange: handleChange,
    result,
    inputId,
    inputName,
    invalid = false,
    proximity,
    bbox,
    types,
    readOnly,
    className,
  } = props
  const [state, setState] = useState<GeocoderState>({
    results: [],
    selected: result,
    focus: null,
    loading: false,
    searchTime: new Date(),
    query: result?.place_name || '',
  })
  const geocoderInput = useRef<HTMLInputElement | null>(null)
  const { handleDebounceChange } = useGeocoderSearch({
    proximity,
    bbox,
    types,
    limit: handleAddAddress ? 4 : 5,
    onChange:
      handleChange ||
      ((e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target || {}
        setState({
          ...state,
          loading: true,
          query: value || '',
        })
      }),
    onSuggest: (result: GeocoderResponse | undefined) => {
      if (result && result.features && state.searchTime <= result.search_time) {
        setState({
          searchTime: result.search_time,
          loading: false,
          results: result.features,
          focus: null,
          query: geocoderInput?.current?.value || '',
        })
      }
    },
  })

  const moveFocus = (dir: number) => {
    if (state.loading) return
    setState({
      ...state,
      focus:
        state.focus === null ? 0 : Math.max(0, Math.min(state.results.length, state.focus + dir)),
    })
  }

  const acceptFocus = () => {
    if (state.results.length === state.focus) {
      handleAddAddress?.(state.query)
    } else if (state.focus !== null) {
      handleSelect?.(state.results[state.focus])
      setState({
        results: [],
        selected: state.results[state.focus],
        focus: null,
        loading: false,
        searchTime: new Date(),
        query: state.results[state.focus]?.place_name,
      })
    }
  }

  const clickOption = (result: GeocodeAddress, listLocation: number) => {
    handleSelect?.(result)
    setState({
      results: [],
      selected: result,
      focus: listLocation,
      loading: false,
      searchTime: new Date(),
      query: result?.place_name,
    })
    // focus on the input after click to maintain key traversal
    // if (geocoderInput?.current) {
    //   geocoderInput.current.focus()
    // }
    return false
  }

  const clickClear = () => {
    if (state.loading) {
      return
    }
    handleSelect?.(undefined)
    setState({
      results: [],
      selected: null,
      focus: null,
      loading: false,
      searchTime: new Date(),
      query: '',
    })
    // focus on the input after click to maintain key traversal
    if (geocoderInput?.current) {
      geocoderInput.current.value = ''
      geocoderInput.current.focus()
    }
    return false
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        moveFocus(-1)
        break
      case 'ArrowDown':
        e.preventDefault()
        moveFocus(1)
        break
      case 'Enter':
        e.preventDefault()
        if (state.results.length > 0 && state.focus == null) {
          clickOption(state.results[0], 0)
        }
        acceptFocus()
        break
    }
  }

  const inputClassNameList: string[] = ['mapboxgl-ctrl-geocoder--input border-start-0 form-control']
  if ((state?.selected?.place_name || geocoderInput?.current?.value) && !readOnly) {
    inputClassNameList.push('border-end-0')
  }
  if (!access_token || invalid) {
    inputClassNameList.push('is-invalid')
  }
  const prependClassName = [
    'input-group-text border-end-0',
    invalid ? 'border-danger' : '',
    readOnly ? 'disabled' : '',
  ]
    .join(' ')
    .trim()
  const appendClassName = [
    'input-group-text text-body border-start-0',
    invalid ? 'border-danger' : '',
  ]
    .join(' ')
    .trim()
  const input: React.ReactElement = (
    <>
      <div className="input-group">
        <span className={prependClassName}>
          <FontAwesomeIcon icon={faSearch} />
        </span>
        <input
          type="text"
          ref={geocoderInput}
          id={inputId}
          name={inputName}
          className={inputClassNameList.join(' ').trim()}
          onChange={handleDebounceChange}
          onKeyDown={onKeyDown}
          onFocus={(e) => e.target.select()}
          onBlur={handleBlur}
          placeholder="Search"
          value={state?.query}
          autoComplete="address"
          readOnly={readOnly}
        />
        {(state?.selected || geocoderInput?.current?.value) && !readOnly && (
          <button className={appendClassName} type="button" onClick={clickClear}>
            {!state.loading ? (
              <FontAwesomeIcon icon={faTimes} />
            ) : (
              <>
                <FontAwesomeIcon icon={faSpinner} pulse />
                <span className="visually-hidden">Loading...</span>
              </>
            )}
          </button>
        )}
      </div>
      {!access_token && (
        <small className="form-text text-danger">Not Authorized - No Mapbox Token.</small>
      )}
    </>
  )

  useEffect(() => {
    setState({
      results: [],
      selected: result,
      focus: null,
      loading: false,
      searchTime: new Date(),
      query: result?.place_name || '',
    })
  }, [result])

  return (
    <div className={`mapboxgl-ctrl-geocoder mapboxgl-ctrl ${className || ''}`.trim()}>
      {input}
      {!!state.query && (
        <ul className="list-group">
          {state.results.length > 0 &&
            state.results.map((result, i) => (
              <button
                onClick={() => clickOption(result, i)}
                className={`list-group-item list-group-item-action ${
                  i === state.focus ? 'active' : ''
                }`.trim()}
                key={result.id}
              >
                <div>
                  <div className="fw-bold">{result.text}</div>
                  <small className="text-body-tertiary">{result.place_name}</small>
                </div>
              </button>
            ))}
          {!state.loading && !state.selected && state.results.length === 0 && (
            <button
              className={`list-group-item list-group-item-action new-suggestion ${
                state.results.length === state.focus ? 'active' : ''
              }`.trim()}
            >
              <div className="position-relative">
                <div className="fw-bold">Couldn't find anything close to your address.</div>
                <small className="text-body-tertiary">Please enter a valid location.</small>
                <div className="position-absolute end-0 top-0 my-2 me-2">
                  <FontAwesomeIcon icon={faQuestion} className="text-secondary" size="lg" />
                </div>
              </div>
            </button>
          )}
          {!state.loading && !state.selected && handleAddAddress && (
            <button
              onClick={() => handleAddAddress(state?.query)}
              className={`list-group-item list-group-item-action list-group-item-light new-suggestion ${
                state.results.length === state.focus ? 'active' : ''
              }`.trim()}
            >
              <div>
                <div className="fw-bold">Can't find what you're looking for?</div>
                <div>Add a new venue or address.</div>
                <div className="position-absolute end-0 top-0 my-2 me-2">
                  <FontAwesomeIcon icon={faSearch} className="text-secondary" size="lg" />
                </div>
              </div>
            </button>
          )}
        </ul>
      )}
    </div>
  )
}
